import { VossProductType } from '../../Constants/VOSS/vossProductType';
import { percentChange, roundToTwoDecimals, sumRounded } from '../../Utils/decimalUtils';
import type { BulkApplyPlan } from './bulkAdjustment';
import type { Adjustments } from './PriceSimulation.types';
import type {
    CalculatedPlan,
    CalculatedProduct,
    CalculatedTier,
    CalculatedTierPoint,
    Calculations,
    VisibleProductInstance,
    WorksheetFilters,
} from './PriceSimulationWorksheet.types';
import { matchesStatusFilter } from './statusFilter';
import type { PackageWorksheet } from './worksheetDataTransformer';
import { isTiered, PRICING_SCHEME } from './priceSimulationModel';

// Key "modified" off the price delta, not the percentage: a product whose base price is 0 has a 0%
// adjustment even after its price is changed (e.g. 0 -> 50), so a percentage check would miss it (VSCO-3569).
export const VOLUME_THRESHOLD = 0;

const hasVolume = (plan: CalculatedPlan): boolean => plan.quantity === null || plan.quantity > VOLUME_THRESHOLD;

export const isPlanModified = (plan: CalculatedPlan): boolean => (isTiered(plan) ? (plan.tierPoints ?? []).some((tp) => tp.delta !== 0) : plan.delta !== 0);

export const planHasQuantity = (plan: CalculatedPlan): boolean => !isTiered(plan) && Number.isFinite(plan.quantity);

export const currentAnnualValue = (plan: CalculatedPlan): number => (plan.currentPrice ?? 0) * (plan.quantity ?? 0);

export const newAnnualValue = (plan: CalculatedPlan): number => (plan.newListPrice ?? 0) * (plan.quantity ?? 0);

export const tierPlans = (tier: CalculatedTier): CalculatedPlan[] => tier.productGroups.flatMap((group) => group.products).flatMap((product) => product.plans);

export const flattenPlans = (calculations: Calculations): CalculatedPlan[] => calculations.packages.flatMap((pkg) => pkg.tiers).flatMap(tierPlans);

export const billedImpact = (plan: CalculatedPlan): number => {
    const price = plan.currentPrice ?? 0;
    if (isTiered(plan) || price <= 0) return 0;
    return roundToTwoDecimals((plan.revenue ?? 0) * ((plan.newListPrice ?? 0) / price - 1));
};

const annualImpact = (plan: CalculatedPlan): number => roundToTwoDecimals(plan.delta * (plan.quantity ?? 0));

const grossRevenue = (plan: CalculatedPlan): number => {
    const revenue = plan.revenue ?? 0;
    const discount = plan.averageDiscount ?? 0;
    return discount < 100 ? roundToTwoDecimals(revenue / (1 - discount / 100)) : revenue;
};

export const revenueIncrease = (plan: CalculatedPlan): number | null => {
    if (isTiered(plan) || !Number.isFinite(plan.quantity) || !Number.isFinite(plan.revenue)) return null;
    const increase = annualImpact(plan);
    return increase === 0 ? null : increase;
};

export const isPlanHandled = (plan: CalculatedPlan): boolean => isPlanModified(plan) || plan.isKept;

export const toEditableBulkPlans = (plans: CalculatedPlan[]): BulkApplyPlan[] => plans.filter((p) => p.status === 'Draft').map(toBulkPlanFromCalculated);

export const pickBulkReferencePlan = (plans: CalculatedPlan[]): CalculatedPlan | null => {
    const draftPlans = plans.filter((p) => p.status === 'Draft');
    if (draftPlans.length === 0) return null;
    // Prefer a plan with a price above 0 kr: an all-0 kr reference gives nothing to scale a target from.
    const scalablePlans = draftPlans.filter((p) => hasScalablePrice(toBulkPlanFromCalculated(p)));
    const candidates = scalablePlans.length > 0 ? scalablePlans : draftPlans;
    return candidates.reduce((shortest, c) => (c.months < shortest.months ? c : shortest), candidates[0]);
};

export const weightedIncreasePercent = (currentValue: number, newValue: number): number => roundToTwoDecimals(percentChange(currentValue, newValue));

export const toHandledPercentage = (handledCount: number, planCount: number): number => (planCount > 0 ? Math.floor((handledCount * 100) / planCount) : 0);

export const toAnnualValueLeftPercentage = (handledAnnualValue: number, currentAnnualValue: number): number => {
    const left = roundToTwoDecimals(currentAnnualValue - handledAnnualValue);
    return left <= 0 || currentAnnualValue <= 0 ? 0 : Math.max(1, Math.ceil(roundToTwoDecimals((left * 100) / currentAnnualValue)));
};

export const toBulkPlanFromCalculated = (p: CalculatedPlan): BulkApplyPlan => ({
    id: p.id,
    pricingScheme: p.pricingScheme ?? PRICING_SCHEME.Flat,
    currentPrice: p.currentPrice ?? 0,
    breakpoints: (p.tierPoints ?? []).map((tp) => ({ index: tp.index, currentUnitPrice: tp.currentUnitPrice, isIncluded: tp.isIncluded })),
});

export const hasIncludedPrice = (plan: BulkApplyPlan): boolean => (isTiered(plan) ? plan.breakpoints.some((bp) => bp.isIncluded) : plan.currentPrice === 0);

export const hasScalablePrice = (plan: BulkApplyPlan): boolean => (isTiered(plan) ? plan.breakpoints.some((bp) => !bp.isIncluded) : plan.currentPrice !== 0);

export const calculateWorksheet = (packages: PackageWorksheet[], adjustments: Adjustments, filters: WorksheetFilters): Calculations => {
    if (packages.length === 0) {
        return {
            packages: [],
            totalImpact: 0,
            subscriptionImpact: 0,
            transactionImpact: 0,
            billedImpact: 0,
            hasQuantity: false,
            currentAnnualValue: 0,
            newAnnualValue: 0,
            currentMrr: 0,
            newMrr: 0,
            weightedIncrease: 0,
            excludedTieredPlanCount: 0,
            excludedTieredGrossRevenue: 0,
        };
    }

    const { modificationStatuses, selectedPlans, selectedProductTypes, selectedProducts, onlyPlansWithVolume } = filters;
    const statusIds = modificationStatuses.map((s) => s.id);

    let currentSubscriptionAnnual = 0,
        newSubscriptionAnnual = 0,
        currentTransactionAnnual = 0,
        newTransactionAnnual = 0,
        billedImpactTotal = 0,
        excludedTieredPlanCount = 0,
        excludedTieredGrossRevenue = 0;

    const calculatedPackages = packages
        .map((pkg) => {
            const tiers = pkg.tiers
                .map((tier) => {
                    const productGroups = tier.productGroups
                        .map((group) => {
                            let groupCurrentAnnualValue = 0,
                                groupNewAnnualValue = 0,
                                groupBilledImpact = 0;
                            let groupHasAnnualValue = false,
                                groupHasQuantity = false;

                            const products = group.products
                                .filter((product) => {
                                    if (selectedProductTypes.length > 0 && !selectedProductTypes.some((st) => st.id === product.productType)) return false;
                                    if (selectedProducts.length > 0 && !selectedProducts.some((sp) => sp.id === product.productSku)) return false;
                                    return true;
                                })
                                .map((product): CalculatedProduct => {
                                    const allPlans = product.plans.map((plan): CalculatedPlan => {
                                        if (isTiered(plan)) {
                                            const planAdjustment = adjustments[product.id]?.[plan.id];
                                            const tierAdj = planAdjustment?.tiers ?? {};
                                            let planDelta = 0;
                                            let pctSum = 0;
                                            let pctCount = 0;

                                            const tierPoints = [...(plan.tieredPrices ?? [])]
                                                .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
                                                .map((tp): CalculatedTierPoint => {
                                                    const index = tp.index ?? 0;
                                                    const currentUnitPrice = roundToTwoDecimals(tp.currentUnitPrice ?? 0);
                                                    const isIncluded = currentUnitPrice === 0;
                                                    const tierData = tierAdj[index];
                                                    const isKept = tierData !== undefined ? tierData.isKept === true : tp.isKept === true;
                                                    const adjustment = isKept ? 0 : (tierData?.percentage ?? 0);
                                                    const newUnitPrice = isKept
                                                        ? currentUnitPrice
                                                        : roundToTwoDecimals(tierData?.newUnitPrice ?? currentUnitPrice * (1 + adjustment / 100));
                                                    const delta = roundToTwoDecimals(newUnitPrice - currentUnitPrice);
                                                    planDelta = sumRounded([planDelta, delta]);
                                                    if (!isIncluded && adjustment !== 0) {
                                                        pctSum += adjustment;
                                                        pctCount++;
                                                    }
                                                    return { index, upperLimit: tp.upperLimit ?? null, currentUnitPrice, adjustment, newUnitPrice, delta, isIncluded, isKept };
                                                });

                                            return {
                                                ...plan,
                                                pricingScheme: 1,
                                                isKept: tierPoints.some((tp) => tp.isKept),
                                                currentPrice: null,
                                                adjustment: pctCount > 0 ? pctSum / pctCount : 0,
                                                newListPrice: null,
                                                delta: planDelta,
                                                tierPoints,
                                            };
                                        }

                                        const currentPrice = roundToTwoDecimals(plan.currentPrice ?? 0);
                                        const adjustmentData = adjustments[product.id]?.[plan.id];
                                        const isKept = adjustmentData !== undefined ? adjustmentData.isKept === true : plan.isKept;
                                        const adjustment = isKept ? 0 : (adjustmentData?.percentage ?? 0);
                                        const newListPrice = isKept ? currentPrice : roundToTwoDecimals(adjustmentData?.newPrice ?? currentPrice * (1 + adjustment / 100));
                                        const delta = roundToTwoDecimals(newListPrice - currentPrice);
                                        return { ...plan, pricingScheme: 0, isKept, currentPrice, adjustment, newListPrice, delta };
                                    });

                                    const filteredPlans = allPlans.filter((plan) => {
                                        if (selectedPlans.length > 0 && !selectedPlans.some((sp) => sp.id === plan.identifier)) return false;
                                        if (onlyPlansWithVolume && !isPlanHandled(plan) && !hasVolume(plan)) return false;
                                        return matchesStatusFilter(statusIds, plan.status, isPlanModified(plan), plan.isKept);
                                    });

                                    filteredPlans.forEach((plan) => {
                                        if (isTiered(plan)) {
                                            excludedTieredPlanCount++;
                                            excludedTieredGrossRevenue = sumRounded([excludedTieredGrossRevenue, grossRevenue(plan)]);
                                            return;
                                        }
                                        groupHasAnnualValue = true;
                                        if (planHasQuantity(plan)) groupHasQuantity = true;
                                        const currentAnnual = currentAnnualValue(plan);
                                        const newAnnual = newAnnualValue(plan);
                                        groupCurrentAnnualValue = sumRounded([groupCurrentAnnualValue, currentAnnual]);
                                        groupNewAnnualValue = sumRounded([groupNewAnnualValue, newAnnual]);
                                        const planBilledImpact = billedImpact(plan);
                                        billedImpactTotal = sumRounded([billedImpactTotal, planBilledImpact]);
                                        groupBilledImpact = sumRounded([groupBilledImpact, planBilledImpact]);
                                        if (product.productType === VossProductType.Usage) {
                                            currentTransactionAnnual = sumRounded([currentTransactionAnnual, currentAnnual]);
                                            newTransactionAnnual = sumRounded([newTransactionAnnual, newAnnual]);
                                        } else {
                                            currentSubscriptionAnnual = sumRounded([currentSubscriptionAnnual, currentAnnual]);
                                            newSubscriptionAnnual = sumRounded([newSubscriptionAnnual, newAnnual]);
                                        }
                                    });

                                    return {
                                        ...product,
                                        plans: filteredPlans,
                                    };
                                })
                                .filter((product) => product.plans.length > 0);

                            return { ...group, products, groupCurrentAnnualValue, groupNewAnnualValue, groupBilledImpact, groupHasAnnualValue, groupHasQuantity };
                        })
                        .filter((group) => group.products.length > 0)
                        .sort((a, b) => (b.isIncluded ? 1 : 0) - (a.isIncluded ? 1 : 0));

                    const tierCurrentAnnualValue = sumRounded(productGroups.map((g) => g.groupCurrentAnnualValue));
                    const tierNewAnnualValue = sumRounded(productGroups.map((g) => g.groupNewAnnualValue));

                    const tierBilledImpact = sumRounded(productGroups.map((g) => g.groupBilledImpact));

                    const tierHasAnnualValue = productGroups.some((g) => g.groupHasAnnualValue);
                    const tierHasQuantity = productGroups.some((g) => g.groupHasQuantity);

                    return { ...tier, productGroups, tierCurrentAnnualValue, tierNewAnnualValue, tierBilledImpact, tierHasAnnualValue, tierHasQuantity };
                })
                .filter((tier) => tier.productGroups.length > 0);

            return {
                packageId: pkg.packageId,
                packageName: pkg.packageName,
                metricsPeriodStart: pkg.metricsPeriodStart,
                metricsPeriodEnd: pkg.metricsPeriodEnd,
                tiers,
                currentAnnualValue: sumRounded(tiers.map((t) => t.tierCurrentAnnualValue)),
                newAnnualValue: sumRounded(tiers.map((t) => t.tierNewAnnualValue)),
                billedImpact: sumRounded(tiers.map((t) => t.tierBilledImpact)),
                hasQuantity: tiers.some((t) => t.tierHasQuantity),
            };
        })
        .filter((pkg) => pkg.tiers.length > 0);

    const subscriptionImpact = roundToTwoDecimals(newSubscriptionAnnual - currentSubscriptionAnnual);
    const transactionImpact = roundToTwoDecimals(newTransactionAnnual - currentTransactionAnnual);
    const totalCurrentAnnualValue = sumRounded([currentSubscriptionAnnual, currentTransactionAnnual]);
    const totalNewAnnualValue = sumRounded([newSubscriptionAnnual, newTransactionAnnual]);

    return {
        packages: calculatedPackages,
        totalImpact: sumRounded([subscriptionImpact, transactionImpact]),
        subscriptionImpact,
        transactionImpact,
        billedImpact: billedImpactTotal,
        hasQuantity: calculatedPackages.some((pkg) => pkg.hasQuantity),
        currentAnnualValue: totalCurrentAnnualValue,
        newAnnualValue: totalNewAnnualValue,
        currentMrr: roundToTwoDecimals(currentSubscriptionAnnual / 12),
        newMrr: roundToTwoDecimals(newSubscriptionAnnual / 12),
        weightedIncrease: weightedIncreasePercent(totalCurrentAnnualValue, totalNewAnnualValue),
        excludedTieredPlanCount,
        excludedTieredGrossRevenue,
    };
};

// "All tiers" must only touch what the worksheet shows, so it is built from the filtered calculations,
// not from the raw packages.
export const buildVisibleInstancesMap = (calculations: Calculations): Record<string, VisibleProductInstance[]> => {
    const map: Record<string, VisibleProductInstance[]> = {};
    calculations.packages.forEach((pkg) => {
        pkg.tiers.forEach((tier) => {
            tier.productGroups.forEach((group) => {
                group.products.forEach((product) => {
                    map[product.productSku] ??= [];
                    map[product.productSku].push({ groupName: group.groupName, productId: product.id, productName: product.name, plans: product.plans });
                });
            });
        });
    });
    return map;
};
